import React, { ChangeEvent, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Button, Divider, Header, Icon, Image, ImageGroup, Modal, ModalActions, ModalContent } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { useAppContext } from '../../../hooks/AppProvider';
import { getGuaranteeFilesByGuaranteeId, uploadGuaranteeFile } from '../../../services/guarantees.service';
import GuaranteeFile from '../../../types/entities/GuaranteeFile';

interface Props {
    guarantee: { id: string };
    fluid?: boolean; 
    isOpen?: boolean;
    readOnly?: boolean;
}

const MAX_GUARANTEE_FILES = 5;

const GuaranteeFilesModal = ({ guarantee, fluid, isOpen, readOnly = false }: Props) => {

    const { updateParentState } = useAppContext();
    const [ openModal, setOpenModal ] = useState(isOpen);

    const [ guaranteeFiles, setGuaranteeFiles ] = useState<GuaranteeFile[]>([]);
    const [ uploading, setUploading ] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Función para obtener los datos con parámetros
    const fetchGuaranteeFiles = async (guaranteeId: string) => {
        updateParentState({ loading: true });
        try {
            if (!guaranteeId) {
                return;
            }
            const response = await getGuaranteeFilesByGuaranteeId(guaranteeId);
            setGuaranteeFiles(response?.data);
        } catch (error) {
            toast.error("Error al obtener el listado de clientes");
        } finally {
            updateParentState({ loading: false });
        }
    };

    useEffect(() => {
        if (openModal) {
            fetchGuaranteeFiles(guarantee?.id);
        }
        // eslint-disable-next-line
    }, [openModal]);

    useEffect(() => {
        setOpenModal(isOpen);
    }, [isOpen]);

    const handleGuaranteeFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const files = Array.from(input.files ?? []);

        // Allow selecting the same image again after a failed or deleted upload.
        input.value = "";

        if (files.length === 0) {
            return;
        }

        const availableSlots = MAX_GUARANTEE_FILES - guaranteeFiles.length;
        if (files.length > availableSlots) {
            toast.error(`Solo puedes agregar ${availableSlots} evidencia${availableSlots === 1 ? "" : "s"} más.`);
            return;
        }

        setUploading(true);
        updateParentState({ loading: true });
        try {
            // The API accepts one image per request, so upload the selected files in order.
            for (const file of files) {
                const formData = new FormData();
                formData.append("file", file, file.name);

                const response = await uploadGuaranteeFile(guarantee?.id, formData);
                if (response?.status !== 201) {
                    throw new Error("La evidencia de la garantía no se ha cargado con éxito; contacta al área de TI.");
                }

                setGuaranteeFiles(currentFiles => [...currentFiles, response.data]);
            }

            toast.success(files.length === 1
                ? "La evidencia de la garantía se cargó con éxito."
                : `${files.length} evidencias de la garantía se cargaron con éxito.`);
        } catch (error: any) {
            toast.error(error.message ?? "No se pudieron cargar las evidencias.");
        } finally {
            setUploading(false);
            updateParentState({ loading: false });
        }
    };

    const handleOpenModal = (event: SyntheticEvent) => {
        event.preventDefault();
        setOpenModal(true);
    };

    const handleCloseModal = () => setOpenModal(false);

    const handleCancelModal = (event: SyntheticEvent) => {
        event.preventDefault();
        if (readOnly || guaranteeFiles?.length >= 2) {
            setOpenModal(false);    
        } else {
            toast.error("Debes de cargar minimo 2 evidencias antes de proseguir");
        }
    };

    return (
        <>
            <Modal
                closeOnDimmerClick
                closeIcon={true}
                size="small"
                onClose={handleCloseModal}
                open={openModal}
                className="dashboard-inline-close-modal"
                trigger={
                    <Button icon fluid={fluid} primary onClick={ (event) => handleOpenModal(event) }>
                        <Icon name="photo" /> Evidencias (fotos)
                    </Button>
                } >
                <Header icon="photo" content="Evidencia de la garantía" />
                <ModalContent>

                    {!readOnly && (
                        <>
                            <p style={{color: "red"}}>
                                Solo se permite cargar imágenes*<br/>
                                Deberas de subir minimo 2 evidencias y maximo 5*
                            </p>

                            <Button
                                secondary
                                loading={uploading}
                                disabled={uploading || guaranteeFiles.length >= MAX_GUARANTEE_FILES}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Cargar evidencia
                            </Button>
                            <input
                                ref={fileInputRef}
                                style={{display: "none"}}
                                type="file"
                                multiple
                                onChange={handleGuaranteeFile}
                            />

                            <Divider />
                        </>
                    )}

                    <ImageGroup size='tiny'>
                        {guaranteeFiles?.map((file: GuaranteeFile) => (
                            <Image 
                                key={file.id}
                                src={file.fileName} 
                                href={file.fileName}
                                target='_blank' 
                            />
                        ))}
                    </ImageGroup>

                </ModalContent>
                <ModalActions>
                    <Button positive onClick={ (event) => handleCancelModal(event) }>Aceptar</Button>
                </ModalActions>
            </Modal>
        </>
    );
};

export default GuaranteeFilesModal;
